<template>
  <a-drawer
    :title="dramaTitle"
    width="1200"
    placement="right"
    :visible="visible"
    :closable="true"
    @close="onClose"
  >
    <div class="drawer-summary">
      <span>
        总集数{{ summaryDisplay.total_ep }}集 / 付费集数{{ summaryDisplay.paid_ep }}集 / 单集价格{{ summaryDisplay.unit_price }}元
      </span>
    </div>
    <a-table
      class="table-view paid-link-table"
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :pagination="pagination"
      :loading="isLoading"
      :scroll="{ y: drawerBodyHeight }"
      :expandable="paidTableExpandable"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'panel_info'">
          <div class="panel-info-cell">
            <div v-for="(line, idx) in visiblePanelLines(record)" :key="idx" class="panel-info-line">
              {{ line }}
            </div>
          </div>
        </template>
        <template v-else-if="column.key === 'operation'">
          <a v-if="getStatusType(record) === 'ready'" class="op-link" @click.prevent="copyPromotionLink(record)">
            复制推广链接
          </a>
          <span v-else-if="getStatusType(record) === 'generating'" class="op-status">链接生成中</span>
          <span v-else-if="getStatusType(record) === 'failed'" class="op-status">链接生成失败</span>
          <span v-else class="op-status">-</span>
        </template>
        <template v-else>
          {{ record[column.dataIndex] ?? '-' }}
        </template>
      </template>
      <template #expandedRowRender="{ record }">
        <div class="expanded-panel-row" :style="expandedRowGridStyle">
          <div class="expanded-cell expanded-cell--expand" aria-hidden="true" />
          <div class="expanded-cell expanded-cell--placeholder" />
          <div class="expanded-cell expanded-cell--placeholder" />
          <div class="expanded-cell expanded-cell--panel-info">
            <template v-if="extraPanelLines(record).length">
              <div v-for="(line, idx) in extraPanelLines(record)" :key="idx" class="panel-info-line">
                {{ line }}
              </div>
            </template>
            <div v-else class="muted">暂无更多档位</div>
          </div>
          <div class="expanded-cell expanded-cell--placeholder" />
          <div class="expanded-cell expanded-cell--placeholder" />
        </div>
      </template>
    </a-table>
  </a-drawer>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { dysPayLink } from '@/api/copyright'

const visible = ref(false)
const isLoading = ref(false)
const dramaRecord = ref(null)
const summary = ref({})
const dataSource = ref([])

const pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  showTotal: total => `总共 ${total} 条`
})

const dramaTitle = computed(
  () => summary.value?.book_name || dramaRecord.value?.remark_name || dramaRecord.value?.book_name || '剧名'
)

const summaryDisplay = computed(() => {
  const s = summary.value || {}
  const total = s.total_ep != null ? s.total_ep : '-'
  const paid = s.paid_ep != null ? s.paid_ep : '-'
  const price = s.unit_price != null && s.unit_price !== '' ? s.unit_price : '-'
  return { total_ep: total, paid_ep: paid, unit_price: price }
})

const drawerBodyHeight = 520

/** 与展开行网格一致：展开列宽需与 ant-table 展开列视觉对齐 */
const EXPAND_ICON_COL_WIDTH = 48

const columns = [
  { title: '面板名称', dataIndex: 'panel_name', key: 'panel_name', width: 180 },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 120 },
  { title: '面板信息', dataIndex: 'panel_info', key: 'panel_info', ellipsis: true },
  { title: '分销商', dataIndex: 'reseller_name', key: 'reseller_name', width: 240 },
  { title: '操作', key: 'operation', width: 140, align: 'center' }
]

const expandedRowGridStyle = computed(() => {
  const [nameCol, timeCol, , resellerCol, opCol] = columns
  return {
    gridTemplateColumns: `${EXPAND_ICON_COL_WIDTH}px ${nameCol.width}px ${timeCol.width}px minmax(0, 1fr) ${resellerCol.width}px ${opCol.width}px`
  }
})

function normalizeSegmentLine(segment) {
  const no = segment.segment_number != null ? `${segment.segment_number}档` : '-'
  const unlock = segment.unlock_episode != null ? `${segment.unlock_episode}集` : '-'
  const discount = segment.discount != null ? `${segment.discount}折` : '-'
  const pay = segment.user_pay_amount != null ? `${segment.user_pay_amount}元` : '-'
  const unit = segment.discount_unit_price != null ? `${segment.discount_unit_price}元` : '-'
  return `${no} / 解锁${unlock} / ${discount} / 支付金额${pay} / 单集${unit}`
}

function normalizeRow(item) {
  return {
    ...item,
    created_at: item.created_date,
    reseller_name: item.company_name,
    promotion_url: item.advertise_link,
    panel_info_lines: (item.segments || []).map(normalizeSegmentLine)
  }
}

function getStatusType(record) {
  const hasLink = !!record.advertise_link
  if (hasLink) return 'ready'
  if (Number(record.status) === 1) return 'generating'
  return 'failed'
}

const paidTableExpandable = {
  rowExpandable: record => (record.panel_info_lines?.length || 0) > 1
}

function visiblePanelLines(record) {
  const lines = record.panel_info_lines || []
  if (lines.length <= 1) return lines
  return [lines[0]]
}

function extraPanelLines(record) {
  const lines = record.panel_info_lines || []
  if (lines.length <= 1) return []
  return lines.slice(1)
}

function loadList() {
  const record = dramaRecord.value || {}
  isLoading.value = true
  dataSource.value = []
  dysPayLink({
    root_book_id: record.root_book_id,
    book_id: record.book_id,
    page: String(pagination.current),
    page_size: String(pagination.pageSize)
  })
    .then(res => {
      const { code, data } = res
      if (code === 0) {
        summary.value = data?.summary || {}
        dataSource.value = (data?.data || []).map(normalizeRow)
        pagination.total = data?.total || 0
      } else {
        message.error(res.message || '加载失败')
      }
    })
    .catch(() => {
      message.error('加载失败')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function showDrawer(record) {
  dramaRecord.value = record || null
  pagination.current = 1
  visible.value = true
  loadList()
}

function onClose() {
  visible.value = false
}

function handleTableChange(p) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  loadList()
}

function copyPromotionLink(record) {
  const url = record.promotion_url
  if (!url) {
    message.warning('暂无链接')
    return
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(
      () => message.success('已复制推广链接'),
      () => message.error('复制失败')
    )
  } else {
    message.error('当前环境不支持复制')
  }
}

defineExpose({ showDrawer })
</script>

<style scoped>
.drawer-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 1.5;
}
.panel-info-cell .panel-info-line,
.expanded-panel-row .panel-info-line {
  line-height: 1.6;
  margin-bottom: 4px;
}
.panel-info-cell .panel-info-line:last-child,
.expanded-panel-row .panel-info-line:last-child {
  margin-bottom: 0;
}
.expanded-panel-row {
  display: grid;
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 0;
  background: #fafafa;
}
.expanded-cell--expand {
  min-height: 1px;
}
.expanded-cell--placeholder {
  min-height: 1px;
}
.expanded-cell--panel-info {
  min-width: 0;
  padding-right: 8px;
}
.expanded-panel-row .muted {
  color: rgba(0, 0, 0, 0.45);
}
:deep(.paid-link-table .ant-table) {
  table-layout: fixed;
}
.op-link {
  color: #1890ff;
  cursor: pointer;
}
.op-status {
  color: rgba(0, 0, 0, 0.45);
}
</style>
